import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Typography, Grid, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Tab, Tabs, IconButton, Stack, MenuItem,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { selectIsAdminStaff } from '../../store/slices/authSlice';
import MyPointsView from './MyPointsView';

type RuleForm = {
  name: string;
  code: string;
  module: string;
  earnRate: number;
  earnPerAmount: number;
  redeemRate: number;
  minRedeemPoints: number;
  maxRedeemPercent: number;
  isActive: boolean;
};

type RuleRow = RuleForm & { _id: string };

const MODULES = ['all', 'transportation', 'porter', 'on_demand', 'grocery', 'food'];

const EMPTY_RULE: RuleForm = {
  name: '', code: '', module: 'all', earnRate: 1, earnPerAmount: 100,
  redeemRate: 0.5, minRedeemPoints: 100, maxRedeemPercent: 20, isActive: true,
};

export default function LoyaltyPage() {
  const isAdmin = useSelector(selectIsAdminStaff);
  return isAdmin ? <LoyaltyRulesView /> : <MyPointsView />;
}

function LoyaltyRulesView() {
  const { symbol } = useTenantCurrency();
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState<RuleRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RuleRow | null>(null);
  const queryClient = useQueryClient();
  const form = useForm<RuleForm>({ defaultValues: EMPTY_RULE });

  const { data: rulesData, isLoading: rulesLoading, error: rulesError } = useQuery({
    queryKey: ['loyalty-rules'],
    queryFn: async () => (await api.get('/admin/loyalty/rules')).data,
  });

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['loyalty-accounts'],
    queryFn: async () => (await api.get('/admin/loyalty/accounts')).data,
    enabled: tab === 1,
  });

  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['loyalty-transactions'],
    queryFn: async () => (await api.get('/admin/loyalty/transactions')).data,
    enabled: tab === 2,
  });

  const saveRule = useMutation({
    mutationFn: (data: RuleForm) => (editing && editing !== 'new'
      ? api.put(`/admin/loyalty/rules/${editing._id}`, data)
      : api.post('/admin/loyalty/rules', data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty-rules'] });
      setEditing(null);
    },
  });

  const deleteRule = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/loyalty/rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty-rules'] });
      setDeleting(null);
    },
  });

  const openEditor = (row: RuleRow | 'new') => {
    saveRule.reset();
    form.reset(row === 'new' ? EMPTY_RULE : { ...EMPTY_RULE, ...row });
    setEditing(row);
  };

  const ruleColumns: GridColDef<RuleRow>[] = [
    { field: 'name', headerName: 'Rule', flex: 1 },
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'module', headerName: 'Module', width: 130 },
    { field: 'earnRate', headerName: 'Earn Rate', width: 100 },
    { field: 'earnPerAmount', headerName: symbol ? `Per ${symbol}` : 'Per amount', width: 100 },
    { field: 'redeemRate', headerName: symbol ? `Redeem ${symbol}/pt` : 'Redeem /pt', width: 120 },
    { field: 'minRedeemPoints', headerName: 'Min Redeem', width: 110 },
    {
      field: 'isActive', headerName: 'Active', width: 90,
      renderCell: (params) => <Chip label={params.value ? 'Yes' : 'No'} size="small" color={params.value ? 'success' : 'default'} />,
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <IconButton size="small" aria-label="Edit rule" onClick={() => openEditor(params.row)}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" aria-label="Delete rule" onClick={() => setDeleting(params.row)}><Delete fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  const accountColumns: GridColDef[] = [
    {
      field: 'userId', headerName: 'User', flex: 1,
      valueGetter: (_v, row) => (row.userId ? `${row.userId.firstName || ''} ${row.userId.lastName || ''}`.trim() || row.userId.phone : '-'),
    },
    { field: 'points', headerName: 'Points', width: 100 },
    { field: 'tier', headerName: 'Tier', width: 100, renderCell: (params) => <Chip label={params.value as string} size="small" /> },
    { field: 'lifetimePoints', headerName: 'Lifetime', width: 100 },
  ];

  const txColumns: GridColDef[] = [
    { field: 'userId', headerName: 'User', flex: 1, valueGetter: (_v, row) => row.userId?.firstName || '-' },
    { field: 'type', headerName: 'Type', width: 90 },
    {
      field: 'points', headerName: 'Points', width: 90,
      renderCell: (params) => (
        <Typography color={(params.value as number) > 0 ? 'success.main' : 'error.main'}>
          {(params.value as number) > 0 ? '+' : ''}{params.value as number}
        </Typography>
      ),
    },
    { field: 'module', headerName: 'Module', width: 110 },
    { field: 'description', headerName: 'Description', flex: 1 },
    { field: 'createdAt', headerName: 'Date', width: 170, valueFormatter: (v) => new Date(v as string).toLocaleString() },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Loyalty Rules</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Legacy per-module rules. They set the redeem rate for grocery checkout and are used for customer earning
        until Points Settings are saved.
      </Typography>
      {rulesError && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(rulesError, 'Could not load rules')}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Rules" />
          <Tab label="Accounts" />
          <Tab label="Transactions" />
        </Tabs>
        {tab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openEditor('new')}>Add Rule</Button>
        )}
      </Box>

      {tab === 0 && (
        <SerialNoDataGrid rows={rulesData?.data || []} columns={ruleColumns} loading={rulesLoading}
          getRowId={(r) => r._id} autoHeight sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      )}
      {tab === 1 && (
        <SerialNoDataGrid rows={accountsData?.data || []} columns={accountColumns} loading={accountsLoading}
          getRowId={(r) => r._id} autoHeight sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      )}
      {tab === 2 && (
        <SerialNoDataGrid rows={txData?.data || []} columns={txColumns} loading={txLoading}
          getRowId={(r) => r._id} autoHeight sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing === 'new' ? 'Create Loyalty Rule' : 'Edit Loyalty Rule'}</DialogTitle>
        <form onSubmit={form.handleSubmit((d) => saveRule.mutate(d))}>
          <DialogContent>
            {saveRule.error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(saveRule.error, 'Could not save rule')}</Alert>}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}><TextField fullWidth label="Name" {...form.register('name', { required: true })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Code" {...form.register('code', { required: true })} /></Grid>
              <Grid item xs={6}>
                <TextField select fullWidth label="Module" value={form.watch('module')} onChange={(e) => form.setValue('module', e.target.value)}>
                  {MODULES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth label="Earn Rate" type="number" inputProps={{ step: '0.01' }} {...form.register('earnRate', { valueAsNumber: true })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label={symbol ? `Earn Per ${symbol}` : 'Earn Per amount'} type="number" {...form.register('earnPerAmount', { valueAsNumber: true })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label={symbol ? `Redeem ${symbol}/point` : 'Redeem /point'} type="number" inputProps={{ step: '0.01' }} {...form.register('redeemRate', { valueAsNumber: true })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Min Redeem Points" type="number" {...form.register('minRedeemPoints', { valueAsNumber: true })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Max Redeem %" type="number" {...form.register('maxRedeemPercent', { valueAsNumber: true })} /></Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.watch('isActive')} onChange={(e) => form.setValue('isActive', e.target.checked)} />}
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saveRule.isPending}>{editing === 'new' ? 'Create' : 'Save'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete loyalty rule"
        message={`Delete "${deleting?.name || ''}"? Existing point balances are not affected.`}
        confirmLabel="Delete"
        loading={deleteRule.isPending}
        onConfirm={() => deleting && deleteRule.mutate(deleting._id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
